/**
 * CSP (Content Security Policy) Utilities
 *
 * This file provides utilities for CSP-compliant dynamic style and script injection
 * using server-generated nonces.
 */

/**
 * Get the CSP nonce from the server (provided by backend replacement)
 * @returns {string} The CSP nonce or fallback for development
 */
export const getCSPNonce = () => {
  // Backend replaces {{CSP_NONCE_PLACEHOLDER}} with actual nonce
  const nonce = window.cspNonce;

  // Check if placeholder was replaced by server
  if (nonce === '{{CSP_NONCE_PLACEHOLDER}}' || !nonce) {
    return 'fallback-nonce-for-development';
  }

  return nonce;
};

/**
 * Safely inject CSS styles with CSP nonce
 * @param {string} css - The CSS content to inject
 * @param {string} id - Optional ID for the style element
 * @returns {HTMLElement} The created style element
 */
export const injectStyleWithNonce = (css, id = null) => {
  const nonce = getCSPNonce();
  const style = document.createElement('style');

  // Set nonce for CSP compliance
  style.setAttribute('nonce', nonce);

  // Set ID if provided
  if (id) {
    style.setAttribute('id', id);
  }

  // Set CSS content
  style.textContent = css;

  // Append to head
  document.head.appendChild(style);

  return style;
};

/**
 * Safely inject JavaScript with CSP nonce
 * @param {string} js - The JavaScript content to inject
 * @param {string} id - Optional ID for the script element
 * @returns {HTMLElement} The created script element
 */
export const injectScriptWithNonce = (js, id = null) => {
  const nonce = getCSPNonce();
  const script = document.createElement('script');

  // Set nonce for CSP compliance
  script.setAttribute('nonce', nonce);

  // Set ID if provided
  if (id) {
    script.setAttribute('id', id);
  }

  // Set JavaScript content
  script.textContent = js;

  // Append to head
  document.head.appendChild(script);

  return script;
};

/**
 * Configure third-party libraries to use CSP nonce
 * @param {Object} libraryConfig - Configuration object for libraries
 */
export const configureLibrariesWithNonce = (libraryConfig = {}) => {
  // Configure common libraries that might need nonce
  const defaultConfig = {
    // Add library configurations here as needed
    // Example:
    // SomeLibrary: { nonce: getCSPNonce() },
    // AnotherLibrary: { nonce: getCSPNonce() }
  };

  const config = { ...defaultConfig, ...libraryConfig };

  // Apply configurations
  Object.entries(config).forEach(([libraryName, config]) => {
    if (window[libraryName] && typeof window[libraryName].config === 'function') {
      window[libraryName].config(config);
    }
  });
};

/**
 * Check if CSP nonce is available
 * @returns {boolean} True if nonce is available
 */
export const isCSPNonceAvailable = () => {
  return !!window.cspNonce;
};

/**
 * Log CSP compliance status for debugging
 */
export const logCSPStatus = () => {
  const nonce = getCSPNonce();
  const isAvailable = isCSPNonceAvailable();

  console.log('CSP Status:', {
    nonce: nonce.substring(0, 10) + '...', // Only show first 10 chars for security
    isServerGenerated: isAvailable,
    isFallback: !isAvailable
  });

  if (!isAvailable) {
    console.warn(
      'Using fallback CSP nonce. Ensure backend provides window.cspNonce in production.'
    );
  }
};

/**
 * Initialize CSP utilities
 * Call this in your main application entry point
 */
export const initializeCSP = () => {
  // Log CSP status for debugging
  logCSPStatus();

  // Configure any libraries that need nonce
  configureLibrariesWithNonce();

  // Early nonce setup for libraries that might create styles during module loading
  if (typeof window !== 'undefined') {
    const nonce = getCSPNonce();

    // Set up early nonce injection for any existing style elements
    const existingStyles = document.querySelectorAll('style:not([nonce])');
    existingStyles.forEach((style) => {
      style.setAttribute('nonce', nonce);
    });
  }

  // Override global style injection if needed
  if (typeof window !== 'undefined') {
    const nonce = getCSPNonce();

    // Store original setAttribute
    const originalSetAttribute = Element.prototype.setAttribute;

    // Override setAttribute to add nonce to style elements
    Element.prototype.setAttribute = function (name, value) {
      if (name === 'style' && this.tagName === 'STYLE' && !this.hasAttribute('nonce')) {
        this.setAttribute('nonce', nonce);
      }
      return originalSetAttribute.call(this, name, value);
    };

    // Override createElement to add nonce to style elements
    const originalCreateElement = document.createElement;
    document.createElement = function (tagName) {
      const element = originalCreateElement.call(this, tagName);
      if (tagName.toLowerCase() === 'style') {
        element.setAttribute('nonce', nonce);
        // Also override the textContent setter to ensure nonce is always present
        const originalTextContent = Object.getOwnPropertyDescriptor(
          Element.prototype,
          'textContent'
        );
        if (originalTextContent && originalTextContent.set) {
          Object.defineProperty(element, 'textContent', {
            set: function (value) {
              this.setAttribute('nonce', nonce);
              originalTextContent.set.call(this, value);
            },
            get: originalTextContent.get
          });
        }
      }
      return element;
    };

    // Override insertAdjacentHTML to add nonce to style elements
    const originalInsertAdjacentHTML = Element.prototype.insertAdjacentHTML;
    Element.prototype.insertAdjacentHTML = function (position, text) {
      if (text.includes('<style') && !text.includes('nonce=')) {
        text = text.replace(/<style/g, `<style nonce="${nonce}"`);
      }
      return originalInsertAdjacentHTML.call(this, position, text);
    };

    // Override appendChild to add nonce to style elements
    const originalAppendChild = Node.prototype.appendChild;
    Node.prototype.appendChild = function (child) {
      if (child.tagName === 'STYLE' && !child.hasAttribute('nonce')) {
        child.setAttribute('nonce', nonce);
      }
      return originalAppendChild.call(this, child);
    };

    // Override insertBefore to add nonce to style elements
    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function (newNode, referenceNode) {
      if (newNode.tagName === 'STYLE' && !newNode.hasAttribute('nonce')) {
        newNode.setAttribute('nonce', nonce);
      }
      return originalInsertBefore.call(this, newNode, referenceNode);
    };

    // Override replaceChild to add nonce to style elements
    const originalReplaceChild = Node.prototype.replaceChild;
    Node.prototype.replaceChild = function (newChild, oldChild) {
      if (newChild.tagName === 'STYLE' && !newChild.hasAttribute('nonce')) {
        newChild.setAttribute('nonce', nonce);
      }
      return originalReplaceChild.call(this, newChild, oldChild);
    };

    // Add MutationObserver to catch any style elements added after initial setup
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'STYLE' && !node.hasAttribute('nonce')) {
              node.setAttribute('nonce', nonce);
            }
            // Also check for style elements within the added node
            const styleElements = node.querySelectorAll
              ? node.querySelectorAll('style:not([nonce])')
              : [];
            styleElements.forEach((style) => {
              style.setAttribute('nonce', nonce);
            });
          }
        });
      });
    });

    // Start observing
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
};

// Export default object with all utilities
export default {
  getCSPNonce,
  injectStyleWithNonce,
  injectScriptWithNonce,
  configureLibrariesWithNonce,
  isCSPNonceAvailable,
  logCSPStatus,
  initializeCSP
};
